"use client";

import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/navigation";
import { RootState } from "../redux/store";
import {
    setAuthenticationMode,
    setCorporateBusinessDetails,
    setIsLoggedIn,
    setIsLoggedInForTempleReceipt,
    setShowOTPmodal,
    SetUserType,
} from "../redux/authSlice";
import { clearCoupon } from "../redux/couponSlice";
import { resetUserDetails } from "../redux/userDetailsSlice";
import { resetVault } from "../redux/vaultSlice";
import { logout as logoutService } from "../services/auth.services";
import mixpanel from "mixpanel-browser";
const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes in milliseconds

// Logout service (moved outside component, typed)
export const logout = async (): Promise<{ success: boolean; error?: string }> => {
    try {
        // Note: `cookies()` from "next/headers" is server-only; use document.cookie or a client-side alternative
        document.cookie = "authToken=; path=/; max-age=0; secure; samesite=strict";
        console.log("✅ Successfully logged out from Inactivity");
        return { success: true };
    } catch (error) {
        console.error("❌ Error during logout:", error);
        return { success: false, error: "An error occurred during logout" };
    }
};

const InactivityHandler = () => {
    const router = useRouter();
    const dispatch = useDispatch();
    const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);
    const timerRef = useRef<NodeJS.Timeout | null>(null); // Type-safe ref for timer

    // Logout logic
    const handleLogout = useCallback(async () => {
        if (!isLoggedIn) return;

        const res = await logoutService();

        if (res.success) {
            dispatch(SetUserType(""));
            dispatch(setIsLoggedIn(false));
            dispatch(resetUserDetails());
            dispatch(setShowOTPmodal(false));
            dispatch(setIsLoggedInForTempleReceipt(false));
            dispatch(setCorporateBusinessDetails(null));
            dispatch(setAuthenticationMode(null));
            dispatch(resetVault());
            dispatch(clearCoupon());
            localStorage.removeItem("mobile_number");
            localStorage.removeItem("token");
            localStorage.removeItem("isLogIn");
            mixpanel.reset();
            router.push("/", { scroll: false });
        } else {
            console.error("Logout failed:", res.error);
        }
    }, [dispatch, isLoggedIn, router]);

    // Reset inactivity timer
    const resetInactivityTimer = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(handleLogout, INACTIVITY_LIMIT);
    }, [handleLogout]);

    // Setup event listeners
    useEffect(() => {
        if (!isLoggedIn) return; // Early return if not logged in

        const events = ["mousemove", "keypress", "click", "touchstart"] as const;
        events.forEach((event) => window.addEventListener(event, resetInactivityTimer));

        resetInactivityTimer(); // Start timer on mount

        return () => {
            events.forEach((event) => window.removeEventListener(event, resetInactivityTimer));
            if (timerRef.current) clearTimeout(timerRef.current); // Cleanup timer
        };
    }, [resetInactivityTimer, isLoggedIn]); // Depend on isLoggedIn to re-run if login state changes

    return null; // No UI, just logic
};

export default InactivityHandler;